import { useEffect, useState } from 'react';
import { message } from 'antd';

import { queryClient } from 'config/query-client';
import { MANAGERS } from 'events/types';
import { createManager, updateManager } from 'services/managers';
import { NewManagerCustomError } from 'types';

import useTranslation from '../../customHooks/translate';
import CustomDialog from './CustomDialog';
import { Input } from './styled-components/common-components';

interface IProps {
  open: boolean;
  managerToUpdate?: any;
  handleClose: () => void;
}

const initialValues: {
  name: string;
  id?: number;
} = {
  name: '',
};

const AddNewManagerForm = ({ open, handleClose, managerToUpdate }: IProps) => {
  const [manager, setManager] = useState(initialValues);
  const [nameError, setNameError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const translation = useTranslation();

  useEffect(() => {
    setManager(managerToUpdate || initialValues);
  }, [managerToUpdate]);

  const setOnValueChange = (event, propName, isSelect = false) => {
    event.persist && event.persist();
    setManager((prevState) => ({
      ...prevState,
      [propName]: isSelect ? event : event.target.value,
    }));
  };

  const handleSave = () => {
    setLoading(true);
    const method = manager.id ? updateManager : createManager;
    method(manager)
      .then(() => {
        message.success(`${translation.messages.successAdded}`);
        queryClient.invalidateQueries({
          queryKey: [MANAGERS],
          refetchType: 'all',
        });
        setManager(initialValues);
        handleClose();
      })
      .catch((error: NewManagerCustomError) => {
        error.response?.data?.Message &&
          message.error(error.response?.data?.Message);
        const errorMessageName = error.response?.data.errors?.Name?.[0] as
          | string
          | undefined;
        setNameError(errorMessageName || null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleSave();
  };

  return (
    <CustomDialog
      open={open}
      handleClose={handleClose}
      title={
        managerToUpdate
          ? translation.managers.UpdateButton
          : translation.managers.Header
      }
      buttonTitle={
        managerToUpdate
          ? translation.managers.UpdateButton
          : translation.managers.ConfirmButton
      }
      isLoading={loading}
      onSubmit={handleSave}
    >
      <form onSubmit={handleSubmit}>
        <div className="flex flex-col gap-[12px]">
          <Input
            className="d-block"
            type="text"
            maxLength={29}
            onChange={(event) => setOnValueChange(event, 'name')}
            pattern="[+-]?\d+(?:[.,]\d+)?"
            placeholder={translation.managers.LabelText}
            value={manager.name || ''}
            style={{
              borderColor: nameError ? 'red' : '',
            }}
          />
          {nameError && (
            <span className="text-[12px] text-red-600">{nameError}</span>
          )}
        </div>
      </form>
    </CustomDialog>
  );
};

export default AddNewManagerForm;
